import { useLayoutContext } from '@/container/context/layout/layout-context';
import { useNavigate, useParams } from 'react-router-dom';
import { Empty, Menu, Skeleton } from 'antd';
import { MenuItemType } from 'antd/es/menu/interface';
import clsx from 'clsx';
import { memo } from 'react';
import { RiArrowDownSLine } from 'react-icons/ri';
import { MobileSidebarTeamsView } from './mobile-sidebar-teams.view';
import { TeamCard } from './team-card';
import { SidebarTeamsProps } from '@modules/teams/teams.types';
import { AddTeam } from './add-team';
import { useScreenSize } from '@shared/hooks/use-screen';
import { MenuProps } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { capitalize } from '@shared/utils/capitalize';
import { FaChevronRight } from 'react-icons/fa6';

const className: MenuItemType['className'] = 'group pl-1.5! p-0! mx-0! h-10! lg:h-11! w-full! overflow-visible!';

function SidebarTeams(props: SidebarTeamsProps) {
  const { isMobile } = useScreenSize();
  const {
    teams: { data, initialLoading, hasNextPage },
    layoutContext,
  } = props;
  const navigate = useNavigate();
  const { teamId: queryTeamId = '' } = useParams();
  const { closeSidebarMobileDrawer, sidebarCollapsed } = layoutContext;
  const { isSubmenuOverlay, setIsSubmenuOverlay } = useLayoutContext();
  const { t } = useTranslation();

  const teams = data?.data ? [...data.data] : [];
  const items: MenuProps['items'] = [
    {
      key: 'teams',
      label: (
        <div className="text-slate-600 flex justify-between items-center ml-0.5">
          <span className="uppercase leading-4! text-xs tracking-wider">{t('your_teams')}</span>
          <AddTeam />
        </div>
      ),
      expandIcon: ({ isOpen }) => (
        <RiArrowDownSLine size={20} className={`transition-transform ${isOpen ? 'rotate-0' : 'rotate-180'}`} />
      ),
      className: clsx(
        'mx-auto! [&_.ant-menu-submenu-title]:hover:bg-gray-200! [&_.ant-menu-submenu-title]:w-full!',
        '[&_.ant-menu-submenu-title]:my-0! [&_.ant-menu-submenu-title]:mx-0! [&_.ant-menu-submenu-title]:px-0.5!'
      ),
      children:
        teams.length > 0
          ? teams
              .slice(0, 4)
              ?.map((team) => ({
                key: team?.id as string,
                label: <TeamCard {...team} />,
                teamId: team?.id,
                teamName: team?.name,
              }))
              .map(({ teamId, teamName, ...item }) => ({
                ...item,
                className,
                onClick: () => {
                  closeSidebarMobileDrawer();
                  if (queryTeamId === teamId) return;
                  navigate('/settings/team-management/' + teamId + '?name=' + teamName);
                },
              }))
          : [
              {
                key: 'empty',
                className: 'h-auto! p-0!',
                label: <Empty className="justify-self-start! mx-auto!" description={capitalize(t('no_teams_found'))} />,
              },
            ],
    },
  ];

  const handleViewAll = () => {
    if (isMobile) {
      navigate('/settings/team-management');
      closeSidebarMobileDrawer();
      return;
    }

    if (isSubmenuOverlay === 'teams') {
      setIsSubmenuOverlay(null);
    } else {
      setIsSubmenuOverlay('teams');
    }
  };

  if (initialLoading) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton active paragraph={false} />
        <Skeleton active paragraph={false} />
      </div>
    );
  }

  return (
    <>
      <div className={clsx(sidebarCollapsed ? 'hidden' : '', 'pb-2')}>
        <Menu
          className="custom-menu custom-sidebar-menu px-2.5! [&_.ant-menu-title-content]:order-1! [&_.ant-menu-submenu-title]:px-0.5!"
          selectedKeys={[String(queryTeamId)]}
          inlineCollapsed={false}
          defaultOpenKeys={['teams']}
          mode="inline"
          items={items}
        />
        {hasNextPage ? (
          <div
            id="view-all"
            className={clsx(
              'flex items-center ml-3 mt-1.5 gap-1 cursor-pointer w-fit py-1 px-2 rounded-full hover:text-primary-400',
              isSubmenuOverlay === 'teams' ? 'text-primary-600 bg-primary-50' : 'text-text-600',
              hasNextPage ? '' : 'invisible'
            )}
            onClick={handleViewAll}
          >
            <p className="inline cursor-pointer text-xs tracking-wider rounded-xl uppercase!">{t('view_all')}</p>
            <FaChevronRight size={10} style={{ transform: isSubmenuOverlay === 'teams' ? 'rotate(180deg)' : '' }} />
          </div>
        ) : null}
      </div>

      <div className={clsx(sidebarCollapsed ? '' : 'hidden')}>
        <MobileSidebarTeamsView {...props} />
      </div>
    </>
  );
}

export const SidebarTeamsView = memo(SidebarTeams);
